/* global rwgps */
import { TrackLocation } from "data/records";
import _ from "lodash";
import React, { useEffect, useState } from "react";

const TRouteMetricsContext = React.createContext(null);

export const getHighlightSliceIndexes = (selectedFeature) => {
  const highlightSlice = _.get(selectedFeature, "data.highlightSlice");
  if (!highlightSlice) {
    return null;
  }
  return highlightSlice.map(TrackLocation.closestIndex);
};

export const TRouteMetricsProvider = ({ troute, objectType, selectedFeature, children }) => {
  const [data, setData] = useState(null);
  const [sliceMetrics, setSliceMetrics] = useState(null);
  const [surfaceTypes, setSurfaceTypes] = useState(null);

  useEffect(() => {
    // init rwgps objects
    const tpsResult = rwgps.TrackPoint.fromJSON(troute.rawPoints);
    const points = tpsResult.trackPoints;
    setData(new rwgps.Data(points));
    calculateMetrics();
  }, []);

  useEffect(() => {
    calculateMetrics();
  }, [selectedFeature, data]);

  const calculateMetrics = _.throttle(
    () => {
      if (!data) {
        return;
      }

      const sliceIndexes = getHighlightSliceIndexes(selectedFeature);
      // old RSP includes the last point in the highlight slice when it does metrics calculation
      const dataSlice = sliceIndexes ? data.slice(sliceIndexes[0], sliceIndexes[1] - sliceIndexes[0] + 1) : data;
      const mc = new rwgps.MetricsCalculator(dataSlice, false);
      const metrics = mc.calculate();
      setSliceMetrics(sliceIndexes ? metrics : null);

      if (objectType === "route" && !surfaceTypes) {
        // since the sidebar surfaces breakdown shouldn't change in response to
        // selectedFeature changes, surfaceTypes should be calculated once ever
        // for the entire track
        if (sliceIndexes) {
          const surfaceMc = new rwgps.MetricsCalculator(data, false);
          surfaceMc.calculate();
          setSurfaceTypes(surfaceMc.surfaces());
        } else {
          setSurfaceTypes(mc.surfaces());
        }
      }
    },
    200,
    { leading: true, trailing: true }
  );

  return (
    <TRouteMetricsContext.Provider value={{ data, sliceMetrics, surfaceTypes }}>
      {children}
    </TRouteMetricsContext.Provider>
  );
};

// higher-order function to consume troute metrics in a component definition
export const withTRouteMetrics = (Component) => (props) =>
  (
    <TRouteMetricsContext.Consumer>
      {({ data, sliceMetrics, surfaceTypes }) => (
        <Component data={data} sliceMetrics={sliceMetrics} surfaceTypes={surfaceTypes} {...props} />
      )}
    </TRouteMetricsContext.Consumer>
  );
